import { useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Divider,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import {
  HistoryVersionSummaryList,
  HistoryVersionSummary,
} from '@liveblocks/react-ui';
import { HistoryVersionPreview } from '@liveblocks/react-lexical';
import { useHistoryVersions } from '@liveblocks/react';

export default function VersionsDialog() {
  const [isOpen, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const onVersionRestore = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{ width: 32, height: 32 }}
      >
        <HistoryIcon fontSize="small" />
      </IconButton>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="lg" // 90vw에 대응하는 너비 설정
        fullWidth
        PaperProps={{
          sx: {
            height: '85vh',
            borderRadius: 3,
            backgroundImage: 'none', // 다크모드 시 배경 최적화
          },
        }}
      >
        {/* 시각적으로 숨겨진 타이틀 (접근성 유지) */}
        <DialogTitle sx={{ display: 'none' }}>Versions</DialogTitle>

        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          <Versions onVersionRestore={onVersionRestore} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function Versions({ onVersionRestore }: { onVersionRestore: () => void }) {
  const [selectedVersionId, setSelectedVersionId] = useState<string>();
  const { versions, isLoading } = useHistoryVersions();

  const selectedVersion = useMemo(
    () => versions?.find((version) => version.id === selectedVersionId),
    [selectedVersionId, versions],
  );

  if (isLoading) return <>loading...</>;

  if (!versions || versions.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          h: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          color: 'text.secondary',
        }}
      >
        No versions yet
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* 프리뷰 영역 */}
      <Box sx={{ flex: 1, height: '100%', minWidth: 0 }}>
        {selectedVersion ? (
          <HistoryVersionPreview
            version={selectedVersion}
            onVersionRestore={onVersionRestore}
            // className 대신 내부 스타일로 조절하거나 필요한 속성 전달
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              color: 'text.secondary',
            }}
          >
            No version selected
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" flexItem />

      {/* 히스토리 리스트 영역 */}
      <Box
        sx={{
          width: 250,
          height: '100%',
          overflowY: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        <HistoryVersionSummaryList>
          {versions.map((version) => (
            <HistoryVersionSummary
              key={version.id}
              onClick={() => setSelectedVersionId(version.id)}
              version={version}
              selected={version.id === selectedVersionId}
            />
          ))}
        </HistoryVersionSummaryList>
      </Box>
    </Box>
  );
}
